<template>
    <div>
        <broadcast-screen-layout v-if="showBroadcastLayout" :match="broadcastMatch" :content-align="screen === 'timeout' ? 'bottom' : 'center'">
            <transition name="slide-from-right" mode="out-in">
                <div :key="broadcastContentKey">
                    <timeout v-if="screen === 'timeout'" :match="broadcastMatch" :period-scores-team1="periodScores.team1" :period-scores-team2="periodScores.team2" :periods="periodScores.keys" />
                    <!-- the break screens rotate through the slides that have something to show -->
                    <template v-else>
                        <slide-placeholder v-if="loadingSlide" :slide="loadingSlide" :match="broadcastMatch" />
                        <past-meetings v-else-if="currentSlide === 'past-meetings'" :match="broadcastMatch" :past-matches="pastMatches" />
                        <match-standings-component v-else-if="currentSlide === 'standings'" :match="broadcastMatch" :ranking="ranking" :bracket="bracket" />
                        <mvp-comparison v-else-if="currentSlide === 'players'" :mvp="false" :match="broadcastMatch" :team1="team1" :team2="team2" />
                        <team-comparison v-else-if="currentSlide === 'teams'" :match="broadcastMatch" :team1="team1" :team2="team2" />
                        <mvp-comparison v-else-if="currentSlide === 'mvp'" :mvp="true" :match="broadcastMatch" :team1="team1" :team2="team2" :mvp-player="mvpPlayer" :mvp-team="mvpTeam" />
                        <half-time v-else-if="currentSlide === 'stats'" :match="broadcastMatch" />
                    </template>
                </div>
            </transition>
        </broadcast-screen-layout>
        <transition name="slide-from-left" appear>
            <div v-show="!showBroadcastLayout" class="score-bottom-left">
                <live-score :id="$route.params.id" @setShowScreen="showScreen" @score="liveScore = $event" @started="onMatchStarted" />
            </div>
        </transition>
        <transition name="slide-from-left" appear>
            <div v-show="!showBroadcastLayout" class="stat-right">
                <live-stat :id="$route.params.id" :show-active-players="screen === 'starters'" @setShowScreen="showScreen" />
            </div>
        </transition>
    </div>
</template>

<script>
    import LiveStat from '@/routes/LiveStat'
    import LiveScore from '@/routes/LiveScore'
    import Timeout from '@/components/Timeout.vue'
    import HalfTime from '@/components/HalfTime.vue'
    import MvpComparison from '@/components/MvpComparison.vue'
    import TeamComparison from '@/components/TeamComparison.vue'
    import PastMeetings from '@/components/PastMeetings.vue'
    import MatchStandingsComponent from '@/components/MatchStandingsComponent.vue'
    import BroadcastScreenLayout from '@/components/BroadcastScreenLayout.vue'
    import SlidePlaceholder from '@/components/SlidePlaceholder.vue'
    import matchService from '@/services/match'
    import leagueService from '@/services/league'
    import bracketService from '@/services/bracket'
    import { calculateTeamStats } from '@/utils/teamStats'
    import { prepareRanking } from '@/utils/standings'
    import { subscribeLiveEvents } from '@/utils/liveEvents'

    // The screen on show is reported on every change and this often, so the CMS knows the page is open
    const SCREEN_REPORT_MS = 20000
    // A break-screen slide stays this long from when it is really on screen
    const SLIDE_MS = 20000
    // A slide whose request fails is tried again after these waits, then skipped
    const SLIDE_RETRY_MS = [2000, 4000]
    // The data of the next slide is requested while the current one is on, so it is ready on time. During a
    // break nothing changes in the match, so it is still current when it shows
    const PREFETCH_NEXT_SLIDE = true

    export default {
        components: {
            LiveStat,
            LiveScore,
            Timeout,
            HalfTime,
            MvpComparison,
            TeamComparison,
            PastMeetings,
            MatchStandingsComponent,
            BroadcastScreenLayout,
            SlidePlaceholder
        },
        data() {
            return {
                screen: 'live-score',
                // the slide on screen; -1 while the first one is still loading
                pregameIndex: -1,
                // cancels a slide request that is overtaken by a newer one (screen change, next slide)
                slideRequest: 0,
                // the rotation of the current break screen; a new screen starts another, which ends the retries of this one
                rotation: 0,
                // the slide whose data is loading: its placeholder is on screen meanwhile
                loadingSlide: null,
                // the request of the next slide's data, made while the current one is on: { request, index, ready }
                nextSlide: null,
                matchReady: null,
                pregameTimer: null,
                match: {},
                team1: {},
                team2: {},
                pastMatches: [],
                ranking: [],
                bracket: null,
                mvpPlayer: { stats: [{ pir: 0 }] },
                mvpTeam: null,
                liveScore: null,
                matchLogs: [],
                unsubscribe: null,
                screenReportTimer: null,
                screenReportInFlight: false
            }
        },
        computed: {
            showBroadcastLayout() {
                return !['live-score', 'starters'].includes(this.screen)
            },
            // Slides of the current break screen. Each one requests its data right before it is shown
            // (see showSlide), and is skipped when the request brings nothing to show
            slides() {
                if (this.screen === 'pregame') return ['past-meetings', 'standings', 'players', 'teams']
                if (this.screen === 'half-time') return ['stats']
                if (this.screen === 'full-time') return ['mvp', 'stats', 'standings']
                return []
            },
            currentSlide() {
                return this.pregameIndex >= 0 ? this.slides[this.pregameIndex] || null : null
            },
            // Changes whenever the centre content swaps, so the transition replays
            broadcastContentKey() {
                return this.loadingSlide ? `${this.screen}-loading-${this.loadingSlide}` : `${this.screen}-${this.pregameIndex}`
            },
            // The match payload is only fetched now and then, while the scoreboard gets every basket live,
            // so the break screens take the score from the scoreboard to never show an older one
            broadcastMatch() {
                const live = this.liveScore
                const match = this.match
                if (!live || !match.team1 || !match.team2) return match
                const sameTeams = (!live.team1Id || live.team1Id === match.team1.id) && (!live.team2Id || live.team2Id === match.team2.id)
                if (!sameTeams) return match
                return { ...match, team1Score: live.team1Score, team2Score: live.team2Score }
            },
            // Points per period from the players' stats, or from the play-by-play logs when those are further ahead
            periodScores() {
                const fromStats = {
                    team1: this.getPeriodPoints(this.match.team1),
                    team2: this.getPeriodPoints(this.match.team2)
                }
                const fromLogs = this.getLogPeriodPoints(this.matchLogs, this.match)
                const total = ({ team1, team2 }) => [team1, team2].reduce((sum, periods) => sum + Object.values(periods).reduce((a, b) => a + b, 0), 0)
                const source = total(fromLogs) > total(fromStats) ? fromLogs : fromStats
                return this.alignPeriodScores(source.team1, source.team2)
            }
        },
        watch: {
            screen(newVal) {
                this.reportScreen()
                // the timeout screen shows the period scores: fresh match and logs
                if (newVal === 'timeout') {
                    this.refreshMatch()
                    this.fetchMatchLogs(this.$route.params.id)
                }
                if (newVal === 'pregame' || newVal === 'full-time' || newVal === 'half-time') {
                    this.startPregameRotation()
                } else {
                    this.stopPregameRotation()
                }
            }
        },
        created() {
            this.loadAllData()
            // the stats operator can switch the screen from the CMS
            this.unsubscribe = subscribeLiveEvents(this.$route.params.id, {
                screen: data => this.forceScreen(data && data.screen),
                // the match colour of a team changed in the stats app: the screens take it from the match
                score: data => {
                    if (data && data.colorsOnly) this.refreshMatch()
                }
            })
            this.reportScreen()
            this.screenReportTimer = setInterval(this.reportScreen, SCREEN_REPORT_MS)
        },
        mounted() {
            if (this.screen === 'pregame' || this.screen === 'full-time' || this.screen === 'half-time') this.startPregameRotation()
        },
        beforeDestroy() {
            this.stopPregameRotation()
            clearInterval(this.screenReportTimer)
            if (this.unsubscribe) this.unsubscribe()
        },
        methods: {
            // The match (teams, league, score) for the header and the timeout screen. The break-screen
            // slides request their own data when they come up, see loadSlide
            loadAllData() {
                const matchId = this.$route.params.id
                this.matchReady = this.$store.dispatch('match/fetchMatch', { matchId }).then(match => {
                    this.match = match
                    this.computeMvp(match)
                    return match
                })
                this.fetchMatchLogs(matchId)
            },
            refreshMatch() {
                return this.$store.dispatch('match/fetchMatch', { matchId: this.$route.params.id, force: true }).then(match => {
                    this.match = match
                    this.computeMvp(match)
                    return match
                })
            },
            // Fresh data for one slide; resolves to whether there is something to show
            loadSlide(slide) {
                return (this.matchReady || this.refreshMatch()).then(() => {
                    const match = this.match
                    if (slide === 'past-meetings') return this.fetchPastMatches(match).then(() => this.pastMatches.length > 0)
                    if (slide === 'standings') return this.fetchStandings(match)
                    if (slide === 'players' || slide === 'teams') {
                        return this.fetchTeams(match).then(() => Boolean(this.team1.players && this.team2.players))
                    }
                    // stats, mvp: the match with its players' stats
                    return this.refreshMatch().then(fresh => Boolean(fresh && fresh.team1 && fresh.team2))
                })
            },
            // The data of a slide, tried again when the request fails; resolves to whether there is something to
            // show (false also when every try failed)
            loadSlideWithRetry(slide, rotation) {
                const started = Date.now()
                const attempt = (tryNo) => this.loadSlide(slide)
                    .then((hasData) => {
                        console.info(`Live data: the ${slide} slide loaded in ${Date.now() - started}ms (try ${tryNo})${hasData ? '' : ', nothing to show'}`)
                        return hasData
                    })
                    .catch((error) => {
                        const wait = SLIDE_RETRY_MS[tryNo - 1]
                        console.warn(`Live data: the ${slide} slide could not load (try ${tryNo}, after ${Date.now() - started}ms)`, error)
                        if (wait === undefined || rotation !== this.rotation) return false
                        return new Promise(resolve => setTimeout(resolve, wait)).then(() => (rotation === this.rotation ? attempt(tryNo + 1) : false))
                    })
                return attempt(1)
            },
            // Shows the first slide from `start` on that has something to show: its placeholder while its fresh data
            // loads, then the slide itself, which stays SLIDE_MS from that moment on. A slide with nothing to show
            // (or that failed every try) is skipped for the next one
            showSlide(start) {
                const slides = this.slides
                if (!slides.length) return
                clearTimeout(this.pregameTimer)
                const request = ++this.slideRequest
                const next = this.nextSlide
                this.nextSlide = null
                const attempt = (step) => {
                    if (request !== this.slideRequest) return
                    // nothing to show at all: the screen stays empty and tries again in a while
                    if (step >= slides.length) {
                        this.loadingSlide = null
                        this.pregameIndex = -1
                        this.pregameTimer = setTimeout(() => this.showSlide(start), SLIDE_MS)
                        return
                    }
                    const index = (start + step) % slides.length
                    const prefetched = step === 0 && next && next.index === index ? next : null
                    // the placeholder only while the data is really on its way, and never over the same slide coming
                    // round again (a break screen of one slide): that one stays on while it refreshes
                    const onScreen = this.pregameIndex === index && !this.loadingSlide
                    if (!onScreen && (!prefetched || !prefetched.ready)) this.loadingSlide = slides[index]
                    const loading = prefetched ? prefetched.request : this.loadSlideWithRetry(slides[index], this.rotation)
                    loading.then((hasData) => {
                        if (request !== this.slideRequest) return
                        if (!hasData) {
                            attempt(step + 1)
                            return
                        }
                        this.loadingSlide = null
                        this.pregameIndex = index
                        this.pregameTimer = setTimeout(() => this.showSlide(index + 1), SLIDE_MS)
                        this.prefetchSlide((index + 1) % slides.length)
                    })
                }
                attempt(0)
            },
            // Requests the data of the slide that comes next, while the current one is on
            prefetchSlide(index) {
                if (!PREFETCH_NEXT_SLIDE || this.slides.length < 2) return
                const next = { index, ready: false, request: null }
                next.request = this.loadSlideWithRetry(this.slides[index], this.rotation).then((hasData) => {
                    next.ready = true
                    return hasData
                })
                this.nextSlide = next
            },
            fetchMatchLogs(matchId) {
                return matchService.getMatchLogs(matchId).then(resp => {
                    this.matchLogs = Array.isArray(resp.data) ? resp.data : []
                }).catch(() => {
                    // keep the logs we already have, so a failed refresh doesn't blank the chart
                })
            },
            fetchTeams(match) {
                return Promise.all([
                    this.$store.dispatch('match/fetchTeam', { teamId: match.team1.id, force: true }),
                    this.$store.dispatch('match/fetchTeam', { teamId: match.team2.id, force: true })
                ]).then(([team1, team2]) => {
                    calculateTeamStats(team1)
                    calculateTeamStats(team2)
                    this.team1 = team1
                    this.team2 = team2
                })
            },
            fetchPastMatches(match) {
                return matchService.getMatches(1, 3, { team: `[{ team1: ${match.team1.id}, team2: ${match.team2.id} }, { team1: ${match.team2.id}, team2: ${match.team1.id} }]` }).then(resp => {
                    // played meetings only, never the match being streamed
                    const pastMatches = resp.data.content.filter(m => String(m.id) !== String(this.$route.params.id) && (m.scoreTeam1 > 0 || m.scoreTeam2 > 0))
                    this.pastMatches = pastMatches
                })
            },
            // a playoffs or cup match: its bracket; else (or a cup with no bracket made) the ranking of the league
            fetchStandings(match) {
                const isCup = Boolean(match.cup || match.isCup)
                const scope = isCup
                    ? { seasonId: match.seasonId, cup: true, cupLeagueId: match.cupLeague ? match.cupLeague.id : null }
                    : (match.phase === 'playoffs' && match.league ? { seasonId: match.seasonId, leagueId: match.league.id } : null)
                const bracket = scope && match.seasonId
                    ? bracketService.getBracket(scope).then(resp => resp.data || null).catch(() => null)
                    : Promise.resolve(null)
                return bracket.then(found => {
                    this.bracket = found
                    if (found) return true
                    if (!match.league) return false
                    return this.fetchRanking(match).then(() => this.ranking.length > 0)
                })
            },
            fetchRanking(match) {
                // Early in the season matches are in the placements phase, whose ranking is separate from the regular season's
                const phase = match.phase === 'placements' ? 'placements' : 'regular_season'
                return leagueService.getRankingByLeague(match.league.id, match.seasonId, phase).then(resp => {
                    this.ranking = prepareRanking(resp.data)
                })
            },
            computeMvp(match) {
                let mvpPlayer = { stats: [{ pir: 0 }] }
                let mvpTeam = null
                const isTeam1Winning = match.team1Score > match.team2Score
                const winningTeamPlayers = (isTeam1Winning ? match.team1?.players : match.team2?.players) || []
                const winningTeamNumber = isTeam1Winning ? 1 : 2

                winningTeamPlayers.forEach((player) => {
                    if (player.stats[0] && mvpPlayer.stats[0].pir < player.stats[0].pir) {
                        mvpPlayer = player
                        mvpTeam = winningTeamNumber
                    }
                })

                this.mvpPlayer = mvpPlayer
                this.mvpTeam = mvpTeam
            },
            // return computed period points without padding
            getPeriodPoints(team) {
                if (!team || !team.players) return {}
                const periodPoints = {}
                team.players.forEach(player => {
                    const ps = player?.stats?.[0]?.periodScore
                    if (ps) {
                        Object.entries(ps).forEach(([period, points]) => {
                            periodPoints[period] = (periodPoints[period] || 0) + points
                        })
                    }
                })
                return periodPoints
            },
            getLogPeriodPoints(logs, match) {
                const points = { team1: {}, team2: {} }
                const team1Id = match?.team1?.id
                const team2Id = match?.team2?.id
                ;(logs || []).forEach(log => {
                    const team = log.teamId === team1Id ? 'team1' : (log.teamId === team2Id ? 'team2' : null)
                    const period = Number(log.period)
                    if (!team || !Number.isFinite(period) || period <= 0) return
                    const made = (Number(log.freeThrowsMade) || 0) + 2 * (Number(log.twoPointsMade) || 0) + 3 * (Number(log.threePointsMade) || 0)
                    if (made > 0) points[team][period] = (points[team][period] || 0) + made
                })
                return points
            },
            // every period either team has scored in; a team with no points in it shows 0
            alignPeriodScores(t1, t2) {
                const norm = (obj) =>
                    Object.keys(obj || {})
                        .map(k => Number(k))
                        .filter(n => Number.isFinite(n) && n > 0)
                        .sort((a, b) => a - b)

                const baseKeys = [...new Set([...norm(t1), ...norm(t2)])].sort((a, b) => a - b)
                const build = (obj, keys) => keys.reduce((acc, k) => {
                    const v = (obj || {})[String(k)]
                    acc[k] = (v === undefined || v === null) ? 0 : v
                    return acc
                }, {})

                return {
                    team1: build(t1, baseKeys),
                    team2: build(t2, baseKeys),
                    keys: baseKeys
                }
            },
            // One report at a time: a report that hangs must not pile up connections next to the event stream
            reportScreen() {
                if (this.screenReportInFlight) return
                this.screenReportInFlight = true
                matchService.reportLiveScreen(this.$route.params.id, this.screen)
                    .catch(() => {})
                    .finally(() => {
                        this.screenReportInFlight = false
                    })
            },
            // The screen the operator picked in the CMS, as it is (until the next change of the game)
            forceScreen(screen) {
                const screens = ['pregame', 'live-score', 'starters', 'timeout', 'half-time', 'full-time']
                if (!screens.includes(screen)) return
                console.info(`Live data: screen set from the CMS (${screen})`)
                this.screen = screen
            },
            // the clock has run: the pregame screens are over (any other screen stays as it is)
            onMatchStarted(reason) {
                if (this.screen !== 'pregame') return
                console.info(`Live data: the pregame ends (${reason})`)
                this.screen = 'live-score'
            },
            showScreen(screen) {
                // The clock ran out on a period: always show the timeout screen (never toggle it off)
                if (screen === 'period-end') {
                    this.screen = 'timeout'
                    return
                }
                if ((this.screen === 'timeout' || this.screen === 'half-time') && screen === 'timeout') {
                    this.screen = 'live-score'
                } else {
                    this.screen = screen
                }
            },
            startPregameRotation() {
                this.stopPregameRotation()
                this.pregameIndex = -1
                this.showSlide(0)
            },
            stopPregameRotation() {
                this.slideRequest++
                this.rotation++
                this.loadingSlide = null
                this.nextSlide = null
                clearTimeout(this.pregameTimer)
                this.pregameTimer = null
            }
        }
    }
</script>

<style>
.score-bottom-left {
    position: fixed;
    bottom: 10px;
    left: 10px;
    z-index: 1001;
    width: 770px;
}

.stat-right {
    position: fixed;
    bottom: 10px;

    /* 12px after the scoreboard, which ends at 800px */
    left: 812px;
    right: 22px;
    z-index: 1000;
}

/* every stat card: the scoreboard's height, with its sponsor (.live-sponsor) 12px after it */
.live-card {
    width: 674px;
    height: 200px;
    margin: 0 !important;
    background-color: #232323;
    color: white;
}

/* the team logos of the overlays: on a white disc, as on the rest of the site */
.live-logo {
    width: 80px !important;
    height: 80px !important;
    padding: 6px;
    border-radius: 50%;
    background: #fff;
    object-fit: contain;
}

.live-logo--lg {
    width: 140px !important;
    height: 140px !important;
    padding: 10px;
}

.live-logo--sm {
    width: 40px !important;
    height: 40px !important;
    padding: 3px;
}

/* Left-side overlays slide in from the left edge, right-side ones from the right edge */
.slide-from-left-enter-active,
.slide-from-right-enter-active {
    transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s ease-out;
}

.slide-from-left-leave-active,
.slide-from-right-leave-active {
    transition: transform 0.4s ease-in, opacity 0.4s ease-in;
}

.slide-from-left-enter,
.slide-from-left-leave-to {
    transform: translateX(calc(-100% - 20px));
    opacity: 0;
}

.slide-from-right-enter,
.slide-from-right-leave-to {
    transform: translateX(100vw);
    opacity: 0;
}
</style>
